// FreshnessChip — "Synced from FieldRoutes 12 min ago", small muted text at the
// far right of the header, red once the sync is older than a day. Reads the
// board's per-day `day_sync` (RT's leg; falls back to the older appointment
// freshness) and posts to the dispatch-refresh endpoint when it exists, keeping
// a spinner until the worker's run completes (≤ 55 s). Older than 60 minutes on
// open → refreshes itself once per day per browser session.
// A tenant with a working sync never sees a banner: the board shows one only
// when a day was NEVER synced or a refresh just failed.
import React, { useEffect, useRef, useState } from "react";
import { RefreshCw } from "lucide-react";
import { toast } from "react-toastify";
import { dispatchRefresh, freshnessOf, ageWords } from "../../../api/dispatchApi";
import "./dispatch-b.css";

const AUTO_REFRESH_AFTER_SECONDS = 60 * 60;
const RED_AFTER_SECONDS = 24 * 60 * 60;

export default function FreshnessChip({ board, day, onRefreshed, onState }) {
  const fresh = freshnessOf(board);
  // RT's refresh endpoint ships with the board's day_sync block: its presence is the switch.
  const canRefresh = Boolean(board && board.day_sync && typeof board.day_sync === "object");
  const [busy, setBusy] = useState(false);
  const [lastResult, setLastResult] = useState(null); // 'completed' | 'timeout' | 'failed'
  const [tick, setTick] = useState(0);
  const autoRan = useRef(false);

  useEffect(() => { const t = setInterval(() => setTick((n) => n + 1), 60000); return () => clearInterval(t); }, []);

  const refresh = async (auto = false) => {
    if (busy) return;
    setBusy(true);
    try {
      const res = await dispatchRefresh(day, { waitSeconds: 40 });
      if (!res) return;
      if (res.status === "not_fieldroutes") { setLastResult("completed"); return; }
      setLastResult(res.status);
      if (res.status === "completed") {
        if (!auto) toast.success(`Refreshed from FieldRoutes — ${res.run?.records_written ?? 0} ${vocabStops(res)} updated`);
        onRefreshed?.();
      } else if (res.status === "timeout") {
        toast.info("FieldRoutes is still syncing this day — the board re-reads in a moment.");
        setTimeout(() => onRefreshed?.(), 15000);
      } else {
        toast.error(res.detail || "FieldRoutes refresh failed — showing the last synced routes.");
      }
    } finally {
      setBusy(false);
    }
  };

  // Auto-refresh on open when the day's sync is older than an hour (once per day per session).
  useEffect(() => {
    if (autoRan.current || !canRefresh || !fresh.isFieldroutes) return;
    const key = `dispatch.autoRefresh.${day}`;
    let done = false;
    try { done = sessionStorage.getItem(key) === "1"; } catch { /* private mode */ }
    if (done) return;
    if (fresh.status === "never" || (fresh.ageSeconds != null && fresh.ageSeconds > AUTO_REFRESH_AFTER_SECONDS)) {
      autoRan.current = true;
      try { sessionStorage.setItem(key, "1"); } catch { /* private mode */ }
      refresh(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canRefresh, fresh.isFieldroutes, fresh.status, fresh.ageSeconds, day]);

  // Tell the board whether a banner is warranted: never synced, or the refresh just failed.
  useEffect(() => {
    onState?.({ never: fresh.isFieldroutes && fresh.status === "never", failed: lastResult === "failed", busy, canRefresh, fresh });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fresh.status, fresh.isFieldroutes, lastResult, busy, canRefresh]);

  if (!fresh.isFieldroutes) return <span className="dfresh dfresh--live" role="status">Live</span>;

  // eslint-disable-next-line no-unused-vars
  const _t = tick; // re-render the age every minute
  const text = busy
    ? "Refreshing from FieldRoutes…"
    : fresh.status === "never" || !fresh.lastSyncedAt
      ? "Never synced from FieldRoutes"
      : `Synced from FieldRoutes ${ageWords(fresh.ageSeconds)}`;
  const tone = busy ? "busy" : fresh.status === "never" ? "never" : fresh.ageSeconds != null && fresh.ageSeconds > RED_AFTER_SECONDS ? "old" : "ok";

  return (
    <span className={`dfresh dfresh--${tone}`} role="status" aria-live="polite" title={fresh.lastSyncedAt ? new Date(fresh.lastSyncedAt).toLocaleString() : undefined}>
      <span className="dfresh__text">{text}</span>
      {canRefresh ? (
        <button type="button" className="dfresh__btn" onClick={() => refresh(false)} disabled={busy} aria-label="Refresh from FieldRoutes now">
          <RefreshCw size={12} className={busy ? "dfresh__spin" : undefined} aria-hidden="true" />
          {busy ? "" : "Refresh"}
        </button>
      ) : null}
    </span>
  );
}

const vocabStops = (res) => (Number(res?.run?.records_written) === 1 ? "stop" : "stops");
